import React from "react";

import { ArrowRight, CheckCircle2 } from "lucide-react";

import type { ActionPlanBucket } from "./actionPlanBuckets";
import { resolveTemplate } from "./signalCTAs";
import CommonModal from "../../Components/Modal";
import type {
  SignalGuide,
  SignalGuideLink,
  SignalTier,
} from "../../Store/Reducers/SignalFeedSlice";

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Detail text for a plan step: drop the "Owner does this." prefix when a
 * chip already shows the owner. */
function stepBodyWithoutOwnerPrefix(
  body: string,
  owner: string | null | undefined,
  ownerLabel: string | null | undefined
): string {
  if (!body || !owner || owner === "you" || !ownerLabel) return body;
  return body
    .replace(new RegExp(`^${escapeRegExp(ownerLabel)} does this\\.\\s*`), "")
    .trim();
}

const TIER_CHIP: Record<SignalTier, { label: string; className: string }> = {
  critical: { label: "Critical", className: "bg-red-100 text-red-700" },
  warning: { label: "Warning", className: "bg-amber-100 text-amber-800" },
  opp: { label: "Opportunity", className: "bg-teal-100 text-teal-800" },
  maint: { label: "Maintenance", className: "bg-slate-100 text-slate-600" },
};

export interface SignalGuideContentProps {
  guide: SignalGuide;
  accountName: string;
  signalTitle: string;
  tier: SignalTier;
  /** The firing's triggered fields plus the parameter values in force,
   * for {{field}} / {{parameter-id}} placeholders (see templateFields). */
  fields?: Record<string, unknown> | null;
  /** The card's own CTA, offered as the guide's main action. */
  primaryLabel?: string;
  onPrimary?: () => void;
  onStepLink?: (link: SignalGuideLink) => void;
  onClose?: () => void;
  /** Admin preview: links render but do nothing. */
  preview?: boolean;
  /** Titles of the steps this user has ticked. */
  progress?: string[];
  /** Tick / untick a step (users only; never in an admin preview). */
  onToggleStep?: (step: number, title: string, done: boolean) => void;
  /** When set, chrome treats this as an approved action plan (not a legacy
   * Action Guide). The bucket itself is admin vocabulary and is never shown. */
  planBucket?: ActionPlanBucket | string | null;
  /** Admin preview only: field markers placed next to the part of a step
   * (or the "clears when" box) that each setting produces. */
  markers?: {
    step?: (index: number, part: "title" | "body" | "link") => React.ReactNode;
    clears?: React.ReactNode;
  };
}

/**
 * An Action Guide / action-plan step list as users see it — shared by the
 * card pop-up and Signal Administration's editor preview.
 */
export const SignalGuideContent: React.FC<SignalGuideContentProps> = ({
  guide,
  accountName,
  signalTitle,
  tier,
  fields,
  primaryLabel,
  onPrimary,
  onStepLink,
  onClose,
  preview = false,
  progress = [],
  onToggleStep,
  planBucket = null,
  markers,
}) => {
  const tierChip = TIER_CHIP[tier] ?? TIER_CHIP.maint;
  const why = resolveTemplate(guide.why, fields);
  const tickable = !preview && !!onToggleStep;
  const doneCount = guide.steps.filter(st =>
    progress.includes(st.title)
  ).length;
  const isPlan = Boolean(planBucket);
  return (
    <div className="bg-white">
      <div className="border-b border-gray-100 px-6 pb-4 pt-5">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
              {isPlan ? "Action plan" : "Action guide"} · {accountName}
            </div>
            <h2 className="mt-1 text-[20px] font-extrabold leading-tight tracking-tight text-gray-900">
              {guide.title || (isPlan ? "Untitled plan" : "Untitled guide")}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11.5px] font-semibold text-gray-600">
              <span
                className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${tierChip.className}`}
              >
                {tierChip.label}
              </span>
              {signalTitle}
            </div>
            {tickable && guide.steps.length > 0 && (
              <div className="mt-3 flex items-center gap-2.5">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-brand transition-all"
                    style={{
                      width: `${Math.round((doneCount / guide.steps.length) * 100)}%`,
                    }}
                  />
                </div>
                <span className="text-[11.5px] font-semibold tabular-nums text-gray-500">
                  {doneCount} of {guide.steps.length}
                </span>
              </div>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              title="Close"
              aria-label="Close guide"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-lg leading-none text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 px-6 py-5">
        {why && (
          <div className="rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400">
              Why you&apos;re seeing this
            </div>
            <p className="m-0 text-[13px] leading-relaxed text-gray-700">
              {why}
            </p>
          </div>
        )}

        <ol className="m-0 flex list-none flex-col gap-0 p-0">
          {guide.steps.map((step, index) => (
            <li
              key={index}
              className="relative grid grid-cols-[28px_minmax(0,1fr)] gap-3 pb-4 last:pb-0"
            >
              {index < guide.steps.length - 1 && (
                <span
                  aria-hidden
                  className="absolute bottom-0 left-[13px] top-7 border-l-2 border-gray-100"
                />
              )}
              {tickable ? (
                <input
                  type="checkbox"
                  checked={progress.includes(step.title)}
                  onChange={e =>
                    onToggleStep?.(index, step.title, e.target.checked)
                  }
                  aria-label={`Step ${index + 1}: ${step.title}`}
                  className="mt-1 h-[18px] w-[18px] cursor-pointer accent-brand"
                />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-tint text-[12px] font-bold text-brand-dark">
                  {index + 1}
                </span>
              )}
              <div className="min-w-0 pt-0.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <div
                    className={`text-[13.5px] font-bold ${
                      tickable && progress.includes(step.title)
                        ? "text-gray-400 line-through"
                        : "text-gray-900"
                    }`}
                  >
                    {resolveTemplate(step.title, fields) || "Untitled step"}
                    {markers?.step?.(index, "title")}
                  </div>
                  {step.owner && step.owner !== "you" && step.owner_label && (
                    <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                      {step.owner_label}
                    </span>
                  )}
                  {step.owner === "you" && isPlan && (
                    <span className="inline-flex rounded-full bg-brand-faint px-2 py-0.5 text-[10px] font-bold text-brand">
                      You
                    </span>
                  )}
                </div>
                {(() => {
                  const bodyText = resolveTemplate(
                    stepBodyWithoutOwnerPrefix(
                      step.body,
                      step.owner,
                      step.owner_label
                    ),
                    fields
                  );
                  if (!bodyText && !markers?.step) return null;
                  if (!bodyText) return markers?.step?.(index, "body") ?? null;
                  return (
                    <p className="m-0 mt-0.5 text-[12.5px] leading-relaxed text-gray-600">
                      {bodyText}
                      {markers?.step?.(index, "body")}
                    </p>
                  );
                })()}
                {step.link && (
                  <button
                    type="button"
                    onClick={() =>
                      !preview && step.link && onStepLink?.(step.link)
                    }
                    className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-semibold text-brand hover:underline"
                  >
                    Open {step.link.label}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                  </button>
                )}
                {step.link && markers?.step?.(index, "link")}
              </div>
            </li>
          ))}
        </ol>

        {guide.clears_when && (
          <div className="flex gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <CheckCircle2
              className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700"
              aria-hidden
            />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-800">
                This clears when
              </div>
              <p className="m-0 mt-0.5 text-[12.5px] leading-relaxed text-emerald-900">
                {resolveTemplate(guide.clears_when, fields)}
                {tickable && " Ticking steps doesn't clear it on its own."}
                {markers?.clears}
              </p>
            </div>
          </div>
        )}
      </div>

      {(primaryLabel || onClose) && (
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-6 py-4">
          {primaryLabel && (
            <button
              type="button"
              onClick={() => !preview && onPrimary?.()}
              className="inline-flex items-center rounded-lg bg-brand px-3.5 py-2 text-[12.5px] font-semibold text-white shadow-sm hover:bg-brand-dark"
            >
              {primaryLabel}
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-[12.5px] font-semibold text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface SignalGuideModalProps
  extends Omit<SignalGuideContentProps, "onClose"> {
  isOpen: boolean;
  onClose: () => void;
}

const SignalGuideModal: React.FC<SignalGuideModalProps> = ({
  isOpen,
  onClose,
  ...content
}) => {
  if (!isOpen) return null;
  return (
    <CommonModal
      isOpen
      toggle={onClose}
      onCloseButton={onClose}
      showHeader={false}
      usePortal
      size="2xl"
      className="!max-w-[560px] !rounded-2xl overflow-hidden shadow-2xl"
      contentClassName="!p-0 overflow-hidden"
    >
      <div className="max-h-[85vh] overflow-y-auto">
        <SignalGuideContent {...content} onClose={onClose} />
      </div>
    </CommonModal>
  );
};

export default SignalGuideModal;
